/* CharmBuilder – Italian Charm Bracelet Builder (Vanilla JS, ohne Abhängigkeiten).
 * Vorschau: SVG-Ring aus maxSlots Gliedern in Perspektive, Charm-Bilder liegen auf den Gliedern.
 * Warenkorb: Basis-Armband + Charms als Zeilen mit gemeinsamer _bundle_id über die Ajax-Cart-API.
 */
class CharmBuilder {
  constructor({ charms = [], maxSlots = 15, baseVariantId = null, basePrice = 0, baseTitle = 'Italian Charm Armband', container = null, onChange = null, labels = {}, currency = 'EUR' } = {}) {
    if (!Array.isArray(charms)) throw new TypeError('charms muss ein Array sein');
    this.maxSlots = Math.max(1, Number(maxSlots) || 15);
    this.baseVariantId = baseVariantId; this.basePrice = Number(basePrice) || 0; this.baseTitle = baseTitle;
    this.currency = currency;
    this.catalog = new Map();
    charms.forEach((c) => this.catalog.set(String(c.id), { ...c, id: String(c.id), slots: Math.max(1, Math.round(Number(c.slots) || 1)), price: Number(c.price) || 0 }));
    this.selection = [];
    this.listeners = new Set();
    this.container = container;
    this.busy = false;
    this.labels = Object.assign({
      preview: 'Dein Armband',
      remaining: (n, max) => `${n} von ${max} Slots frei`,
      full: 'Alle Slots belegt',
      add: 'Hinzufügen',
      remove: 'Entfernen',
      empty: 'Noch keine Charms gewählt. Tippe unten auf einen Charm.',
      chosen: 'Gewählt',
      total: 'Gesamt',
      cart: 'In den Warenkorb',
      cartBusy: 'Wird hinzugefügt …',
      cartEmpty: 'Wähle mindestens einen Charm.',
      tooBig: (title, need, free) => `„${title}“ braucht ${need} ${need === 1 ? 'Slot' : 'Slots'}, frei sind nur noch ${free}.`,
      unknown: 'Dieser Charm ist nicht verfügbar.',
      badIndex: 'An dieser Position liegt kein Charm.',
    }, labels);
    if (typeof onChange === 'function') this.subscribe(onChange);
    if (this.container) {
      this.container.addEventListener('click', (e) => this._onClick(e));
      this.render();
    }
  }

  /* ---------- Zustand ---------- */
  getCurrentSlots() { return this.selection.reduce((sum, c) => sum + c.slots, 0); }
  getRemainingSlots() { return this.maxSlots - this.getCurrentSlots(); }
  getSelection() { return this.selection.map((c, index) => ({ ...c, index })); }
  getTotal() { return this.basePrice + this.selection.reduce((s, c) => s + c.price, 0); }
  canAdd(charmId) { const c = this.catalog.get(String(charmId)); return !!c && c.slots <= this.getRemainingSlots(); }
  getState() { return { selection: this.getSelection(), used: this.getCurrentSlots(), remaining: this.getRemainingSlots(), maxSlots: this.maxSlots, total: this.getTotal() }; }

  /* ---------- Aktionen: geben immer ein Ergebnisobjekt zurück, werfen nie ---------- */
  addCharm(charmId) {
    const charm = this.catalog.get(String(charmId));
    if (!charm) return this._result(false, 'unknown', this.labels.unknown);
    const free = this.getRemainingSlots();
    if (charm.slots > free) return this._result(false, 'no-space', this.labels.tooBig(charm.title, charm.slots, free));
    this.selection.push({ ...charm });
    this._emit('add', charm);
    return this._result(true, 'added');
  }
  removeCharm(index) {
    const i = Number(index);
    if (!Number.isInteger(i) || i < 0 || i >= this.selection.length) return this._result(false, 'bad-index', this.labels.badIndex);
    const [removed] = this.selection.splice(i, 1);
    this._emit('remove', removed);
    return this._result(true, 'removed');
  }
  moveCharm(from, to) {
    if (from === to || from < 0 || to < 0 || from >= this.selection.length || to >= this.selection.length) return this._result(false, 'bad-index', this.labels.badIndex);
    const [c] = this.selection.splice(from, 1); this.selection.splice(to, 0, c);
    this._emit('move', c);
    return this._result(true, 'moved');
  }
  reset() { this.selection = []; this._emit('reset'); return this._result(true, 'reset'); }

  /* ---------- Warenkorb ---------- */
  /* Charms je Variante gebündelt, Positionen am Armband als Eigenschaft für die Werkstatt. */
  toCartItems() {
    const byVariant = new Map();
    this.selection.forEach((c, i) => {
      const key = String(c.variantId);
      const item = byVariant.get(key) || { id: c.variantId, quantity: 0, positions: [] };
      item.quantity += 1; item.positions.push(i + 1);
      byVariant.set(key, item);
    });
    return [...byVariant.values()].map((it) => ({ id: it.id, quantity: it.quantity, properties: { Position: it.positions.join(', ') } }));
  }
  /* Basis-Armband + Charms mit gemeinsamer _bundle_id (Unterstrich = im Checkout unsichtbar, in der Bestellung sichtbar). */
  buildCartPayload() {
    const bundleId = 'charm-' + Date.now();
    const items = [];
    if (this.baseVariantId) items.push({ id: this.baseVariantId, quantity: 1, properties: { _bundle_id: bundleId, Konfiguration: 'Individuell zusammengestellt', Charms: this.selection.map((c, i) => `${i + 1}: ${c.title}`).join(' · ') } });
    this.toCartItems().forEach((it) => items.push({ id: it.id, quantity: it.quantity, properties: { _bundle_id: bundleId, 'Position(en)': it.properties.Position, _parent_product: this.baseTitle } }));
    return items;
  }
  async addToCart() {
    if (!this.selection.length) return this._result(false, 'empty', this.labels.cartEmpty);
    if (this.busy) return this._result(false, 'busy');
    this.busy = true; this.render();
    try {
      const items = this.buildCartPayload();
      if (window.theme && window.theme.cart && typeof window.theme.cart.add === 'function') {
        await window.theme.cart.add(items, { open: true });               // Lunora: aktualisiert Drawer + Zähler, öffnet Drawer
      } else {
        const root = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
        const res = await fetch(root + 'cart/add.js', { method: 'POST', headers: { 'Content-Type': 'application/json', Accept: 'application/json' }, body: JSON.stringify({ items }) });
        const data = await res.json();
        if (!res.ok) throw new Error(data.description || data.message || 'Cart error');
        document.dispatchEvent(new CustomEvent('cart:refresh', { detail: data }));
      }
      this._emit('cart');
      return this._result(true, 'cart');
    } catch (err) {
      return this._result(false, 'cart-error', err.message || String(err));
    } finally { this.busy = false; this.render(); }
  }

  /* ---------- Reaktivität ---------- */
  subscribe(fn) { this.listeners.add(fn); return () => this.listeners.delete(fn); }
  _emit(type, charm) { const state = { type, charm, ...this.getState() }; if (this.container) this.render(); this.listeners.forEach((fn) => fn(state)); }
  _result(ok, code, message = '') { return { ok, code, message, used: this.getCurrentSlots(), remaining: this.getRemainingSlots() }; }
  _money(cents) { return new Intl.NumberFormat('de-DE', { style: 'currency', currency: this.currency }).format(cents / 100); }

  /* ---------- Vorschau: Armband-Ring als SVG ---------- */
  /* Glieder auf einer Ellipse, vorne (unten) groß, hinten klein; Zeichenreihenfolge von hinten nach vorn. */
  _braceletSvg() {
    const W = 640, H = 300, cx = W / 2, cy = 150, rx = 262, ry = 96;
    const n = this.maxSlots;
    const slotOwner = []; this.selection.forEach((c, si) => { for (let k = 0; k < c.slots; k++) slotOwner.push({ c, si, part: k }); });
    const links = [];
    for (let i = 0; i < n; i++) {
      const t = (i / n) * 2 * Math.PI + Math.PI / 2;          // Slot 1 vorne in der Mitte
      const x = cx + rx * Math.cos(t), y = cy + ry * Math.sin(t);
      const depth = (Math.sin(t) + 1) / 2;                      // 0 hinten … 1 vorne
      const s = 0.55 + 0.45 * depth;
      const w = 62 * s, h = 46 * s;
      const rot = Math.atan2(ry * Math.cos(t), -rx * Math.sin(t)) * 180 / Math.PI; // Tangente
      links.push({ i, x, y, w, h, rot, depth, owner: slotOwner[i] || null });
    }
    links.sort((a, b) => a.depth - b.depth);
    const esc = (s) => String(s).replace(/[&<>"']/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c]));
    const parts = links.map((l) => {
      const o = l.owner;
      const face = o && o.c.image
        ? `<svg x="${-l.w / 2}" y="${-l.h / 2}" width="${l.w}" height="${l.h}" viewBox="${(o.part * 100) / o.c.slots} 0 ${100 / o.c.slots} 100" preserveAspectRatio="xMidYMid slice"><image href="${esc(o.c.image)}" width="100" height="100" preserveAspectRatio="xMidYMid slice"/></svg>`
        : o
          ? `<rect x="${-l.w / 2 + 3}" y="${-l.h / 2 + 3}" width="${l.w - 6}" height="${l.h - 6}" fill="var(--color-text)"/><text x="0" y="4" text-anchor="middle" font-size="${(11 * (0.6 + 0.4 * l.depth)).toFixed(1)}" fill="#fff">${esc(String(o.c.title).slice(0, 3))}</text>`
          : `<rect x="${-l.w / 2 + 3}" y="${-l.h / 2 + 3}" width="${l.w - 6}" height="${l.h - 6}" fill="var(--cb-gold-face)"/>`;
      return `<g class="cb__link${o ? ' is-filled' : ''}" transform="translate(${l.x.toFixed(1)} ${l.y.toFixed(1)}) rotate(${l.rot.toFixed(1)})"${o ? ` data-remove="${o.si}" role="button" tabindex="0" aria-label="${esc(this.labels.remove)}: ${esc(o.c.title)}"` : ''}>
        <rect x="${-l.w / 2}" y="${-l.h / 2}" width="${l.w}" height="${l.h}" fill="var(--cb-gold)" stroke="var(--cb-gold-dark)" stroke-width="1.5"/>
        ${face}
        <rect x="${-l.w / 2 + 1}" y="${-l.h / 2 + 1}" width="${l.w - 2}" height="${l.h - 2}" fill="none" stroke="#fff" stroke-opacity="${(0.15 + 0.25 * l.depth).toFixed(2)}" stroke-width="1"/>
        <text x="0" y="${l.h / 2 - 5}" text-anchor="middle" font-size="${(9 * (0.6 + 0.4 * l.depth)).toFixed(1)}" fill="var(--cb-gold-dark)" opacity="${o ? 0 : 0.9}">${l.i + 1}</text>
      </g>`;
    });
    return `<svg class="cb__svg" viewBox="0 0 ${W} ${H}" role="img" aria-label="${esc(this.labels.preview)}"><ellipse cx="${cx}" cy="${cy + 8}" rx="${rx + 34}" ry="${ry + 34}" fill="var(--color-surface)"/>${parts.join('')}</svg>`;
  }

  render() {
    if (!this.container) return;
    const { remaining, used, maxSlots, total } = this.getState();
    const esc = (s) => String(s).replace(/[&<>"']/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c]));
    const chips = this.selection.map((c, i) => `<li class="cb__chip">${c.image ? `<img src="${esc(c.image)}" alt="">` : '<span class="cb__noimg"></span>'}<span>${i + 1}. ${esc(c.title)}</span><button type="button" class="cb__chip-remove" data-remove="${i}" aria-label="${esc(this.labels.remove)}: ${esc(c.title)}">×</button></li>`).join('');
    const picker = [...this.catalog.values()].map((c) => {
      const fits = c.slots <= remaining;
      const why = fits ? '' : ` title="${esc(this.labels.tooBig(c.title, c.slots, remaining))}"`;
      return `<li class="cb__charm${fits ? '' : ' is-disabled'}">
        ${c.image ? `<img src="${esc(c.image)}" alt="" loading="lazy">` : '<span class="cb__noimg cb__noimg--big" aria-hidden="true"></span>'}
        <span class="cb__charm-title">${esc(c.title)}</span>
        <span class="cb__charm-meta">${c.price ? this._money(c.price) + ' · ' : ''}${c.slots} ${c.slots === 1 ? 'Slot' : 'Slots'}</span>
        <button type="button" class="btn btn--small" data-add="${esc(c.id)}"${why} ${fits ? '' : 'disabled aria-disabled="true"'}>${esc(this.labels.add)}</button>
      </li>`;
    }).join('');
    this.container.innerHTML = `
      <div class="cb">
        <div class="cb__head">
          <h3 class="cb__title">${esc(this.labels.preview)}</h3>
          <p class="cb__count" aria-live="polite">${remaining === 0 ? esc(this.labels.full) : esc(this.labels.remaining(remaining, maxSlots))}</p>
        </div>
        <div class="cb__bar" aria-hidden="true"><span style="width:${(used / maxSlots) * 100}%"></span></div>
        <div class="cb__preview">${this._braceletSvg()}</div>
        ${this.selection.length ? `<div class="cb__chosen"><span class="cb__label">${esc(this.labels.chosen)}</span><ul class="cb__chips">${chips}</ul></div>` : `<p class="cb__empty">${esc(this.labels.empty)}</p>`}
        <p class="cb__warning" role="alert" hidden></p>
        <div class="cb__cta">
          <p class="cb__total"><span class="cb__label">${esc(this.labels.total)}</span><strong>${this._money(total)}</strong></p>
          <button type="button" class="btn btn--large${this.busy ? ' is-loading' : ''}" data-cart ${this.selection.length && !this.busy ? '' : 'disabled'}>${this.busy ? esc(this.labels.cartBusy) : esc(this.labels.cart)}</button>
        </div>
        <ul class="cb__picker">${picker}</ul>
      </div>`;
  }

  async _onClick(e) {
    const add = e.target.closest('[data-add]'); const rm = e.target.closest('[data-remove]'); const cart = e.target.closest('[data-cart]');
    if (!add && !rm && !cart) return;
    const res = add ? this.addCharm(add.dataset.add) : rm ? this.removeCharm(rm.dataset.remove) : await this.addToCart();
    const warn = this.container.querySelector('.cb__warning');
    if (warn) { warn.textContent = res.ok ? '' : res.message; warn.hidden = res.ok || !res.message; }
  }
}
if (typeof module !== 'undefined') module.exports = CharmBuilder;
if (typeof window !== 'undefined') window.CharmBuilder = CharmBuilder;
